import { IUserLeaderboardViewModel, IUserWeightedLeaderboardBreakdown, LeaderboardMetric } from 'fitbeat.models';
import moment from 'moment';
import { TranslateProp } from '../common-types';

interface IIndividualWorkoutInfo {
  dateHeader: string,
  dateSubHeader: string,
  totalScoreForWorkoutFormatted: string,
  contributedScoreForWorkoutFormatted: string,
  contributionSubText: string,
}

export const leaderboardMetricInfo = (
  userLeaderboardData: IUserLeaderboardViewModel,
  t: TranslateProp,
): { header: string, text: string }[] => {
  const infoBulletPoints: { header: string, text: string }[] = [];
  switch (userLeaderboardData.metric) {
    case 'team-weighted-minutes-score':
      infoBulletPoints.push({ header: t('tracking:scoreColumnHeader'), text: t('tracking:minuteScoreInfo') });
      break;
    case 'team-weighted-points-score':
      infoBulletPoints.push({ header: t('tracking:scoreColumnHeader'), text: t('tracking:pointScoreInfo') });
      break;
  }
  if (userLeaderboardData.metric.includes('weighted')) {
    infoBulletPoints.push({
      header: t('tracking:balancingScoreHeader'),
      text: t('tracking:weightedBalancingScoreInfo'),
    });
  }
  infoBulletPoints.push({ header: t('tracking:fairScoringHeader'), text: t('tracking:fairScoringInfo') });
  infoBulletPoints.push({ header: t('tracking:teamSizeHeader'), text: t('tracking:teamSizeInfo') });

  return infoBulletPoints;
};

export const determineTeamsOverviewHeaders = (
  userLeaderboardData: IUserLeaderboardViewModel,
  t: TranslateProp,
) => {
  const calculatedScore = userLeaderboardData.rankings.reduce((total, ranking) => total + ranking.value, 0);
  const myScore = userLeaderboardData.rankings.find((ranking) => ranking.isMine)?.value;
  switch (userLeaderboardData.metric) {
    case 'team-weighted-minutes-score':
      return [
        {
          header: calculatedScore.toFixed(2),
          subHeader: t('tracking:teamTotalOverviewHeader'),
        },
        {
          header: myScore?.toFixed(2) ?? '0',
          subHeader: t('tracking:individualWeightedOverviewHeader'),
        },
      ];
    case 'team-weighted-points-score':
      return [
        {
          header: calculatedScore.toFixed(2),
          subHeader: t('tracking:teamTotalOverviewHeader'),
        },
        {
          header: myScore?.toFixed(2) ?? '0',
          subHeader: t('tracking:individualWeightedOverviewHeader'),
        },
      ];
    default:
      return [];
  }
};

export const determineScoreBreakdownOverviewHeaders = (
  userBreakdown: IUserWeightedLeaderboardBreakdown[],
  t,
  metric: LeaderboardMetric,
) => {
  const totalScore = userBreakdown.reduce((sum, item) => sum + item.totalScore, 0);
  const contributedScore = userBreakdown.reduce((sum, item) => sum + item.calculatedScore, 0);
  const workoutCount = userBreakdown.length;

  return [
    {
      header: contributedScore.toFixed(2),
      subHeader: t('tracking:individualWeightedOverviewHeader'),
    },
    {
      header: totalScore.toFixed(2),
      subHeader: isMinuteBasedMetric(metric)
        ? t('tracking:individualTotalMinutesOverviewHeader')
        : t('tracking:individualTotalPointsOverviewHeader'),
    },
    {
      header: workoutCount.toString(),
      subHeader: t('tracking:individualWorkoutCountOverviewHeader'),
    },
  ];
};

export const calculateFormattedTotals = (
  userBreakdown: IUserWeightedLeaderboardBreakdown[] | undefined,
): { totalScoreFormatted: string, contributionScoreFormatted: string } => {
  const totalScore = userBreakdown?.reduce((sum, item) => sum + item.totalScore, 0) ?? 0;
  const totalScoreFormatted = totalScore % 1 !== 0
    ? totalScore.toFixed(2)
    : totalScore.toString();
  const contributedScore = userBreakdown?.reduce((sum, item) => sum + item.calculatedScore, 0) ?? 0;
  const contributionScoreFormatted = contributedScore % 1 !== 0
    ? contributedScore.toFixed(2)
    : contributedScore.toString();

  return {
    totalScoreFormatted,
    contributionScoreFormatted,
  };
};

export const determineUserBreakdownMetrics = (
  leaderboardMetric: LeaderboardMetric,
  score: string,
): { metricAbr: string, metric: string } => {
  if (score === '1') {
    const shortMetricAbr = isMinuteBasedMetric(leaderboardMetric) ? 'min' : 'point';
    const shortMetric = isMinuteBasedMetric(leaderboardMetric) ? 'minute' : 'point';

    return {
      metricAbr: shortMetricAbr,
      metric: shortMetric,
    };
  }
  const metricAbr = isMinuteBasedMetric(leaderboardMetric) ? 'min' : 'points';
  const metric = isMinuteBasedMetric(leaderboardMetric) ? 'minutes' : 'points';

  return {
    metricAbr,
    metric,
  };
};

export const determineIndividualWorkoutInfo = (
  workoutBreakdown: IUserWeightedLeaderboardBreakdown,
  leaderboardMetric: LeaderboardMetric,
): IIndividualWorkoutInfo => {
  const date = moment(workoutBreakdown.dateCompleted);
  const day = date.format('D');
  const month = date.format('MMM');
  const hours = date.format('H');
  const minutes = date.format('mm');

  const totalScoreForWorkoutFormatted = workoutBreakdown.totalScore % 1 !== 0
    ? workoutBreakdown.totalScore.toFixed(2)
    : workoutBreakdown.totalScore.toString();

  const { metric, metricAbr } = determineUserBreakdownMetrics(leaderboardMetric, totalScoreForWorkoutFormatted);

  const contributedScoreForWorkoutFormatted = workoutBreakdown.calculatedScore % 1 !== 0
    ? workoutBreakdown.calculatedScore.toFixed(2)
    : workoutBreakdown.calculatedScore.toString();
  const contributionSubText = `${totalScoreForWorkoutFormatted} ${metric} ÷ ${workoutBreakdown.teamSize} ${
    workoutBreakdown.teamSize > 1
      ? 'members'
      : 'member'
  }`;
  return {
    dateHeader: `${day} ${month}`,
    dateSubHeader: `${hours}:${minutes}`,
    contributedScoreForWorkoutFormatted,
    totalScoreForWorkoutFormatted: `${totalScoreForWorkoutFormatted} ${metricAbr}`,
    contributionSubText,
  };
};

export const isMinuteBasedMetric = (leaderboardMetric: LeaderboardMetric) => leaderboardMetric.includes('minutes');
